import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import moment from 'moment-mini';
import { constants } from 'constants/constants';
import { StepService } from 'services/professional/step/step.service';

export abstract class TestRegistrationMixin {
  protected constructor(private stepServiceBase: StepService) {}

  private selectedCountry: any = {};
  private fieldsIds: Map<string, number>;
  private actualElements: any[] = [];

  private readonly FIELD_IDS_MAP = {
    ZIP_CODE: 'ZIP_CODE',
    VALID_FROM: 'VALID_FROM',
    VALID_TO: 'VALID_TO',
    COUNTRY: 'COUNTRY',
    STATE: 'STATE',
  };

  private getElementById(elements, id) {
    return elements.find(({ id: elementId }) => elementId === id);
  }

  private getControlById(controls, id) {
    return controls[id];
  }

  updateFieldValue({ field: { id, value }, elements, controls }, { previewMode, disabledByRoleIgnore = false }) {
    this.actualElements = elements;
    id = Number(id);
    const _getElementById = this.getElementById.bind(this, elements);
    const _getControlById = this.getControlById.bind(this, controls);
    // ValidFrom
    if (id === this.getFieldIdFromMap(this.FIELD_IDS_MAP.VALID_FROM)) {
      const validTo = _getElementById(this.getFieldIdFromMap(this.FIELD_IDS_MAP.VALID_TO));
      if (validTo) {
        const dateArr = [value, validTo.minFieldValue].filter(date => date).map(date => moment(date));
        validTo.minDate = dateArr.length ? moment.max(...dateArr).format('YYYY-MM-DD') : null;
      }
    }
    // ValidTo
    if (id === this.getFieldIdFromMap(this.FIELD_IDS_MAP.VALID_TO)) {
      const validFrom = _getElementById(this.getFieldIdFromMap(this.FIELD_IDS_MAP.VALID_FROM));
      if (validFrom) {
        validFrom.maxDate = value;
      }
    }
    // Country
    if (id === this.getFieldIdFromMap(this.FIELD_IDS_MAP.COUNTRY)) {
      const countryField = _getElementById(this.getFieldIdFromMap(this.FIELD_IDS_MAP.COUNTRY));
      const selectedCountry = countryField.elementOptions.find(
        ({ id: elementOption }) => elementOption === Number(value),
      );
      if (selectedCountry) {
        const isCountryChanged = !this.selectedCountry || this.selectedCountry.id !== selectedCountry.id;
        this.selectedCountry = selectedCountry;
        // Update ZipCode
        if (this.getFieldIdFromMap(this.FIELD_IDS_MAP.ZIP_CODE)) {
          const zipCodeControls = _getControlById([this.getFieldIdFromMap(this.FIELD_IDS_MAP.ZIP_CODE)]);
          zipCodeControls.setValue(zipCodeControls.value);
        }
        const stateField = _getElementById(this.getFieldIdFromMap(this.FIELD_IDS_MAP.STATE));
        if (this.selectedCountry.name && stateField && stateField.fieldInstanceId) {
          const stateControls = _getControlById([this.getFieldIdFromMap(this.FIELD_IDS_MAP.STATE)]);
          this.stepServiceBase
            .getFieldInstanceOptions(stateField.fieldInstanceId, { countryName: this.selectedCountry.name })
            .subscribe(response => {
              stateField.elementOptions = response.map(({ optionId, name }) => ({
                id: optionId,
                name,
              }));
              delete stateField.hidden;
              const stateValue = stateControls.value;
              stateControls.setValue(null);
              if (!isCountryChanged && stateValue) {
                setTimeout(() => stateControls.setValue(stateValue));
              }
              if (selectedCountry && !response.length) {
                stateField.hidden = true;
              }
              if (!previewMode && (disabledByRoleIgnore || !stateField.disabledByRole)) {
                if (response.length) {
                  stateControls.enable();
                } else {
                  stateControls.disable();
                }
              }
            });
        }
      }
    }
  }

  validationMapElements(elements, disabledParam = 'disabled') {
    if (elements.length) {
      return elements.map(field => {
        if (field.type.id === constants.FORM_FIELD_TYPE_IDS.SELECT) {
          if (field.subType === constants.SELECT_SUB_TYPES.COUNTRY) {
            this.setFieldIdMap(this.FIELD_IDS_MAP.COUNTRY, field.id);
            this.selectedCountry =
              field.elementOptions.find(({ id: elementOption }) => elementOption === Number(field.value)) || {};
            return {
              ...field,
              isWatchChangeValue: true,
              isPushInitValue: !!field.value,
            };
          }
          if (field.subType === constants.SELECT_SUB_TYPES.STATE) {
            this.setFieldIdMap(this.FIELD_IDS_MAP.STATE, field.id);
            const country = elements.find(
              ({ type, subType }) =>
                type &&
                type.id === constants.FORM_FIELD_TYPE_IDS.SELECT &&
                subType === constants.SELECT_SUB_TYPES.COUNTRY,
            );
            return {
              ...field,
              [disabledParam]: !field.value || (country && !country.value),
            };
          }
        }
        if (field.type.id === constants.FORM_FIELD_TYPE_IDS.DATE) {
          if ([constants.DATE_SUB_TYPES.VALID_FROM, constants.DATE_SUB_TYPES.VALID_TO].includes(field.subType)) {
            this.setFieldIdMap(
              constants.DATE_SUB_TYPES.VALID_FROM === field.subType
                ? this.FIELD_IDS_MAP.VALID_FROM
                : this.FIELD_IDS_MAP.VALID_TO,
              field.id,
            );
            const dateField = {
              ...field,
              minDate: field.minFieldValue,
              maxDate: field.maxFieldValue,
              isWatchChangeValue: true,
              isPushInitValue: !!field.value,
            };
            return {
              ...dateField,
              validators: [this.dateValidator(dateField)],
            };
          }
        }
        if (field.type.id === constants.FORM_FIELD_TYPE_IDS.TEXT) {
          if (field.subType === constants.TEXT_SUB_TYPES.ZIP_CODE) {
            this.setFieldIdMap(this.FIELD_IDS_MAP.ZIP_CODE, field.id);
            return {
              ...field,
              validators: [this.countryPostalCodeValidator()],
            };
          }
        }
        return field;
      });
    }
  }

  private setFieldIdMap(key: string, id: number): void {
    if (!this.fieldsIds) {
      this.fieldsIds = new Map();
    }
    this.fieldsIds.set(key, id);
  }

  private getFieldIdFromMap(key: string): number | null {
    if (!this.fieldsIds) {
      return null;
    }
    return this.fieldsIds.get(key);
  }

  private countryPostalCodeValidator(text = 'Please enter a valid postal code'): ValidatorFn {
    return (control: UntypedFormControl) => {
      const { value } = control;
      if (!value) {
        return null;
      }

      switch (this.selectedCountry.name) {
        case 'United States':
          return /(^\d{5}$)|(^\d{9}$)|(\d{5}\-\d{4}$)/.test(value) ? null : { invalidPostalCode: text };

        case 'Canada':
          return /(^\w{6}$)|(^[\w\d]{3}\s[\w\d]{3}$)/.test(value) ? null : { invalidPostalCode: text };

        default:
          return null;
      }
    };
  }

  private dateValidator(element) {
    const text = 'Please review your selections for “Valid To / Valid From"';
    return (control: UntypedFormControl) => {
      const { value } = control;
      if (!value) {
        return null;
      }
      const { minDate, maxDate } = this.actualElements.find(({ id }) => id === element.id) || element;

      if ((minDate && moment(minDate).isAfter(value, 'days')) || (maxDate && moment(maxDate).isBefore(value, 'days'))) {
        return { invalidDate: text };
      }
      return null;
    };
  }
}
